import { Injectable } from '@angular/core';
import { CcmSessionData } from '@fi-app/components/core-base';
import { fiSetBrandingColor, fiSetContrastColor, fiSetWidgetColor } from '@fi-shared/utils/branding';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { ISiteDesign } from '@fi-shared/models';
import { fiLoadLink } from '@fi-shared/utils/load-link';
import { FiImageSize } from '@fi-shared/types/image-size';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { isDefined } from '@fi-shared/utils';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

var extendDeep = function extendDeep(dst, obj) {
    for (var key in obj) {
        var value = obj[key];
        if (dst[key] && dst[key].constructor && dst[key].constructor === Object) {
            extendDeep(dst[key], value);
        } else {
            dst[key] = value;
        }
    }
    return dst;
};

@Injectable()
export class SessionService {
    constructor(
        private mediaStorageService: FiMediaStorageService,
        private userDataService: FiUserDataService,
    ) {}

    getSession() {
        return CcmSessionData;
    }

    syncSession() {
        this.getStorage().setItem('CcmSession', JSON.stringify(this.getSession()));
    }
    resetSession() {
        this.getSession().Reset();
        this.removeSessionFromStorage();
        this.syncSession();
        this.userDataService.setUserData(null);
    }
    loadFromLocalStorage() {
        const data = this.getStorage().getItem('CcmSession');
        if (data) {
            this.getSession().Reinitialize(JSON.parse(data));
        }
    }

    getLastLocalSearchData() {
        return CcmSessionData.LocalSearch;
    }
    setLocalSearchData(data) {
        CcmSessionData.LocalSearch = data;
        this.getStorage().setItem('CcmSession', JSON.stringify(this.getSession()));
    }

    private getStorage() {
        return this.userDataService.getStayLoggedIn() ? localStorage : sessionStorage;
    }
    private removeSessionFromStorage() {
        localStorage.removeItem('CcmSession');
        sessionStorage.removeItem('CcmSession');
    }

    private siteDesignLinks: HTMLElement[] = [];

    setSiteDesign(siteDesign: ISiteDesign) {
        this.siteDesignLinks.forEach((el) => el.remove());
        this.siteDesignLinks.length = 0;
        this.siteDesignLinks.push(fiSetBrandingColor(siteDesign.BrandingColor));
        this.siteDesignLinks.push(fiSetContrastColor(siteDesign.ContrastColor));
        this.siteDesignLinks.push(fiSetWidgetColor(siteDesign.WidgetColor));
        this.setBackgroundImages(siteDesign.LandscapeReference);
        this.setLogoIcons(siteDesign.LogoReference);
    }

    setBackgroundImages(landscape: IMediaBankFileReference) {
        const landscapeBackground = landscape
            ? `url("${this.mediaStorageService.getMediaFileUrl(landscape, { size: FiImageSize.Background })}")`
            : '#ffffff';

        const css = `:root{ 
            --fi-branded-image-landscape: ${landscapeBackground};
            }`;

        const style = document.createElement('style');
        style.type = 'text/css';
        style.appendChild(document.createTextNode(css));
        document.head.appendChild(style);
        this.siteDesignLinks.push(style);
    }

    setLogoIcons(logo: IMediaBankFileReference) {
        if (!logo) {
            return;
        }

        const logoUrl = this.mediaStorageService.getMediaFileUrl(logo, { size: FiImageSize.Logo }),
            authLogoUrl = this.mediaStorageService.getMediaFileUrl(logo, { size: FiImageSize.AttachmentPreview });

        this.siteDesignLinks.push(
            fiLoadLink({
                rel: 'icon',
                sizes: '192x192',
                href: logoUrl,
            }),
        );

        this.siteDesignLinks.push(
            fiLoadLink({
                rel: 'apple-touch-icon',
                href: logoUrl,
            }),
        );

        const css = `:root{ 
            --fi-branded-image-logo: url("${authLogoUrl}");
            }`;

        const style = document.createElement('style');
        style.type = 'text/css';
        style.appendChild(document.createTextNode(css));
        document.head.appendChild(style);
        this.siteDesignLinks.push(style);
    }

    //#region Dashboard
    private _isDashboardVisible$ = new BehaviorSubject<boolean>(false);
    get isDashboardVisible$() {
        return this._isDashboardVisible$.asObservable();
    }
    toggleDashboard(newDashboardVisible?: boolean) {
        const prevValue = this._isDashboardVisible$.value;
        const newValue = isDefined(newDashboardVisible) ? newDashboardVisible : !prevValue;
        this._isDashboardVisible$.next(newValue);
    }
    //#endregion
}
